import { useEffect, useRef } from 'react';
import type { ResearchState } from '@galaxis/shared';
import { AREA_META, KIND_LABEL, RARITY_LABEL, rarityClass, startBlock, storagePreview, thousands, type AreaClient, type TechClient } from './research-format.ts';
import { CostLine, lockText } from './TechCard.tsx';

interface Props {
  tech: TechClient;
  area: AreaClient;
  state: ResearchState;
  live: { metal: number; energy: number; metalCapacity: number | null } | null;
  busy: boolean;
  /** Set when the player has to confirm an expensive research. */
  confirming: boolean;
  onStart: (t: TechClient) => void;
  onClose: () => void;
}

/** Detail sheet: effects, what it opens up, what is missing; also the confirmation of an expensive research. */
export function TechDetail({ tech: t, area, state, live, busy, confirming, onStart, onClose }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const prev = useRef<Element | null>(null);
  useEffect(() => {
    prev.current = document.activeElement;
    ref.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose(); }
      if (e.key === 'Tab' && ref.current) {
        const f = [...ref.current.querySelectorAll<HTMLElement>('button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])')];
        if (f.length === 0) return;
        const first = f[0]!, last = f[f.length - 1]!;
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => { document.removeEventListener('keydown', onKey, true); (prev.current as HTMLElement | null)?.focus?.(); };
  }, [onClose]);

  const block = t.status === 'available' ? startBlock(t, area, live) : null;
  const willQueue = area.slots.used >= area.slots.total || area.queue.length > 0;
  const storage = storagePreview(t, state, live?.metalCapacity ?? null);
  const names = new Map(state.techs.map(x => [x.id, x]));
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }} data-testid="tech-detail-backdrop">
      <div ref={ref} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="tech-detail-title"
        className={`sci-fi-panel w-full sm:max-w-lg max-h-[90dvh] overflow-y-auto rounded-t-2xl sm:rounded-2xl border ${rarityClass(t)} p-4 pb-[max(1rem,env(safe-area-inset-bottom))] outline-none`}
        data-testid="tech-detail">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h2 id="tech-detail-title" className="text-base font-semibold text-slate-100">{t.name}</h2>
            <p className="text-xs text-slate-400">
              {AREA_META[t.area].icon} {AREA_META[t.area].label} · {t.tier}. szint · {KIND_LABEL[t.kind]} · {RARITY_LABEL[t.rarity]}
            </p>
          </div>
          <button type="button" className="min-h-11 min-w-11 rounded text-slate-300 hover:text-white" onClick={onClose} aria-label="Bezárás" data-testid="tech-detail-close">✕</button>
        </div>
        <p className="mt-2 text-sm text-slate-300">{t.description}</p>

        <h3 className="mt-3 text-xs font-semibold text-slate-400">Hatás</h3>
        <ul className="text-sm text-emerald-300/90 list-disc list-inside" data-testid="detail-effects">
          {t.unlocks.map(u => <li key={u}>{u}</li>)}
        </ul>
        {storage && <p className="text-xs text-sky-300 mt-1">Raktár (fém): {thousands(storage.from)} → {thousands(storage.to)}</p>}

        <h3 className="mt-3 text-xs font-semibold text-slate-400">Előfeltétel</h3>
        {t.requires.length === 0 ? <p className="text-xs text-slate-500">Nincs.</p> : (
          <ul className="text-xs text-slate-300">
            {t.requires.map(r => {
              const x = names.get(r);
              return <li key={r}>{x?.status === 'done' ? '✓' : '○'} {x?.name ?? r}{x ? ` (${AREA_META[x.area].label}, ${x.tier}. szint)` : ''}</li>;
            })}
          </ul>
        )}

        <h3 className="mt-3 text-xs font-semibold text-slate-400">Ezt nyitja meg</h3>
        {(t.opens ?? []).length === 0 ? <p className="text-xs text-slate-500">Nem nyit meg további technológiát.</p> : (
          <p className="text-xs text-slate-300" data-testid="detail-opens">{(t.opens ?? []).join(', ')}</p>
        )}

        {t.status === 'locked' && <p className="mt-3 text-xs text-amber-400">{lockText(t)}</p>}
        {!t.discoveryOnly && t.status !== 'done' && (
          <div className="mt-3"><CostLine tech={t} area={area} live={live} /></div>
        )}

        {t.status === 'available' && (
          <div className="mt-4">
            {confirming && <p className="text-xs text-amber-300 mb-2" role="alert" data-testid="confirm-text">Ez egy drága kutatás ({thousands(t.effectiveCost.metal)} fém, {thousands(t.effectiveCost.energy)} energia): a költség azonnal levonódik.</p>}
            <button type="button" className="btn-primary min-h-11 w-full disabled:opacity-40" disabled={busy || !!block} data-testid="detail-start"
              onClick={() => onStart(t)}>
              {confirming ? 'Megerősítem' : willQueue ? 'Sorba állítás' : 'Kutatás indítása'}
            </button>
            {block && <p className="text-xs text-amber-400 mt-1">{block}</p>}
          </div>
        )}
      </div>
    </div>
  );
}
